import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NewCustomerContactRequest } from '../../../interfaces/new-customer-requests.model';
import { NewCustomerService } from '../../../services/new-customer.service';
import { digitsOnlyControl, emailFormatValidator, isValidEmail } from '../../../utils/new-customer.utils';

// Contatto principale del nuovo cliente
@Component({
  selector: 'ord-new-customer-contact',
  imports: [MatButtonModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
  templateUrl: './new-customer-contact.html',
  styleUrl: './new-customer-contact.scss',
})
export class NewCustomerContact implements OnInit {
  readonly flow = inject(NewCustomerService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = this.formBuilder.nonNullable.group({
    nominativo: ['', [Validators.required, Validators.maxLength(100)]],
    ruolo: ['', Validators.maxLength(50)],
    telefono: ['', Validators.maxLength(20)],
    email: ['', [Validators.required, emailFormatValidator, Validators.maxLength(100)]],
    note: ['', Validators.maxLength(500)],
  });

  ngOnInit(): void {
    const contact = this.flow.contact();
    if (contact) {
      this.form.setValue({
        nominativo: contact.nominativo,
        ruolo: contact.ruolo ?? '',
        telefono: contact.telefono ?? '',
        email: contact.email ?? '',
        note: contact.note ?? '',
      }, { emitEvent: false });
    }

    digitsOnlyControl(this.form.controls.telefono, this.destroyRef);

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.flow.setContact(this.contact));
  }

  get contact(): NewCustomerContactRequest {
    const value = this.form.getRawValue();
    return {
      nominativo: value.nominativo.trim(),
      ruolo: value.ruolo.trim() || null,
      telefono: value.telefono.trim() || null,
      cellulare: null,
      email: value.email.trim() || null,
      fax: null,
      note: value.note.trim() || null,
      principale: true,
    };
  }

  get valid(): boolean {
    const contact = this.contact;
    return !!contact.nominativo && !!contact.email && isValidEmail(contact.email);
  }

  // Lo step dei dati bancari si apre con le condizioni di pagamento già lette
  continue(): void {
    if (!this.valid || this.flow.loading()) {
      return;
    }

    this.flow.setContact(this.contact);
    this.flow.loadPayments().subscribe({
      next: () => this.flow.go('bank'),
      error: (error: unknown) => {
        const message = this.flow.errorMessage(error);
        if (message) {
          this.snackBar.open(message, 'Chiudi', { duration: 5000 });
        }
      },
    });
  }
}
